import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared";
import { FadeUp } from "@/components/motion/Reveal";
import { getGallery, fileUrl } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { getGallery().then(setItems).catch(() => setItems([])); }, []);

  // Fallback: if there are no admin-uploaded images yet, show the brand photo set
  const fallback = [
    { id: "f1", url: ASSETS.preachingStage, caption: "The Kingdom Standard" },
    { id: "f2", url: ASSETS.preachingPodium, caption: "A fresh word" },
    { id: "f3", url: ASSETS.preachingEvent1, caption: "Next Bold Step" },
    { id: "f4", url: ASSETS.preachingEvent2, caption: "In the room where it happens" },
    { id: "f5", url: ASSETS.groupAdults, caption: "The family" },
    { id: "f6", url: ASSETS.groupYouth, caption: "Next generation" },
    { id: "f7", url: ASSETS.founderPortrait, caption: "Kerby Stivene" },
  ];

  const display = items.length > 0
    ? items.map((it) => ({ id: it.id, url: fileUrl(it.storage_path), caption: it.caption || "" }))
    : fallback;

  return (
    <div data-testid="gallery-page">
      <PageHeader
        testId="gallery-header"
        label="Behind The Scenes"
        title={<>The <span className="text-wutn">Gallery</span></>}
        subtitle="Moments from gatherings, outreach, and life around Wake-Up The Nation."
        image={ASSETS.groupAdults}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {display.map((it, i) => (
              <FadeUp key={it.id} delay={(i % 6) * 0.04}>
                <button
                  onClick={() => setActive(it)}
                  data-testid={`gallery-item-${it.id}`}
                  className="group block w-full aspect-square overflow-hidden bg-zinc-900 relative"
                >
                  <img src={it.url} alt={it.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  {it.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 text-xs text-left text-zinc-200 translate-y-full group-hover:translate-y-0 transition-transform">
                      {it.caption}
                    </div>
                  )}
                </button>
              </FadeUp>
            ))}
          </div>

          {items.length === 0 && (
            <p className="mt-12 text-center text-xs text-zinc-600">
              Fresh photos coming soon — this page updates automatically when new images are uploaded.
            </p>
          )}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-black border border-white/10 rounded-none">
          {active && (
            <div>
              <img src={active.url} alt={active.caption || ""} className="w-full max-h-[80vh] object-contain bg-black" />
              {active.caption && <p className="px-4 py-3 text-sm text-zinc-300 border-t border-white/10">{active.caption}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
